import { useNamespaceSelector } from '@omaze/redux';
import classNames from 'classnames';
import React, { ReactElement, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import styles from './Snackbar.module.css';
import { snackbarDisplayName } from './Snackbar.qa';
import { SNACKBAR_TYPE } from './Snackbar.type';
import { createRemoveAction } from './SnackbarModule/actions/RemoveSnackbarMessage/RemoveSnackbarMessage';
import { selectSnackbarMessages } from './SnackbarModule/selectors/selectSnackbarMessages';
import { ISnackbarMessage } from './SnackbarModule/Snackbar.state';

export enum SNACKBAR_SPEED {
    DEFAULT = 'default',
    SLOW = 'slow',
}

// duration should reflect sum of css fadeout
export const snackbarDuration = {
    [SNACKBAR_SPEED.DEFAULT] : 3000,
    [SNACKBAR_SPEED.SLOW]: 5600,
};

export interface ISnackbarProps {
    className?: string;
    'data-testid'?: string;
    speed?: SNACKBAR_SPEED;
}

export function OzSnackbar (props: ISnackbarProps): ReactElement | null {
    const {
        className,
        'data-testid': dataTestId,
        speed,
    }: ISnackbarProps = props;

    const dispatch: Dispatch = useDispatch();
    const messages: ISnackbarMessage[] = useNamespaceSelector(selectSnackbarMessages);

    useEffect(
        (): undefined | (() => void) => {
            if (!messages.length) {
                return;
            }

            const timeout: Timer = setTimeout((): void => {
                dispatch(createRemoveAction(messages[0].id));
            }, snackbarDuration[speed || SNACKBAR_SPEED.DEFAULT]);

            return (): void => {
                return clearTimeout(timeout);
            };
        }, [messages, dispatch],
    );

    if (!messages.length) {
        return null;
    }

    const { message, type }: ISnackbarMessage = messages[0];

    const snackbarClassNames: string = classNames({
        [styles.OzSnackbar]: true,
        [styles[speed || SNACKBAR_SPEED.DEFAULT]]: true,
        'bg-success': type === SNACKBAR_TYPE.SUCCESS,
        'bg-error': type === SNACKBAR_TYPE.ERROR,
        [className + '']: !!className,
    });

    return (
        <div
            className={`text-white type-body1 px-3 py-4 text-center w-full fixed z-10 bottom-0 left-0 ${snackbarClassNames}`}
            data-testid={dataTestId || ''}
        >
            {message}
        </div>
    );
}

OzSnackbar.displayName = snackbarDisplayName;
